import {
  Contract,
  Interface,
  JsonRpcProvider,
  Signature,
  TypedDataEncoder,
  ZeroAddress,
  concat,
  dataLength,
  getAddress,
  getBytes,
  getCreate2Address,
  hashMessage,
  hexlify,
  isAddress,
  keccak256,
  recoverAddress,
  solidityPacked,
  toBeHex,
  toUtf8Bytes,
  type JsonRpcSigner,
} from "ethers";
import type {
  AppConfig,
  Proposal,
  ProposalSignature,
  OwnerAccessProof,
  SafeSnapshot,
  SafeTransaction,
  TokenBalance,
} from "./types";

export const SAFE_ABI = [
  "function setup(address[] _owners,uint256 _threshold,address to,bytes data,address fallbackHandler,address paymentToken,uint256 payment,address payable paymentReceiver)",
  "function getOwners() view returns (address[])",
  "function getThreshold() view returns (uint256)",
  "function VERSION() view returns (string)",
  "function nonce() view returns (uint256)",
  "function getTransactionHash(address to,uint256 value,bytes data,uint8 operation,uint256 safeTxGas,uint256 baseGas,uint256 gasPrice,address gasToken,address refundReceiver,uint256 _nonce) view returns (bytes32)",
  "function execTransaction(address to,uint256 value,bytes data,uint8 operation,uint256 safeTxGas,uint256 baseGas,uint256 gasPrice,address gasToken,address payable refundReceiver,bytes signatures) payable returns (bool success)",
  "event ExecutionSuccess(bytes32 indexed txHash,uint256 payment)",
  "event ExecutionFailure(bytes32 indexed txHash,uint256 payment)",
] as const;

export const FACTORY_ABI = [
  "function createProxyWithNonce(address _singleton,bytes initializer,uint256 saltNonce) returns (address proxy)",
  "function proxyCreationCode() pure returns (bytes)",
  "event ProxyCreation(address indexed proxy,address singleton)",
] as const;

export const ERC20_ABI = [
  "function balanceOf(address account) view returns (uint256)",
  "function transfer(address to,uint256 amount) returns (bool)",
  "function symbol() view returns (string)",
  "function name() view returns (string)",
  "function decimals() view returns (uint8)",
] as const;

export const SAFE_TX_TYPES = {
  SafeTx: [
    { name: "to", type: "address" },
    { name: "value", type: "uint256" },
    { name: "data", type: "bytes" },
    { name: "operation", type: "uint8" },
    { name: "safeTxGas", type: "uint256" },
    { name: "baseGas", type: "uint256" },
    { name: "gasPrice", type: "uint256" },
    { name: "gasToken", type: "address" },
    { name: "refundReceiver", type: "address" },
    { name: "nonce", type: "uint256" },
  ],
};

export const OWNER_ACCESS_DOMAIN_NAME = "L1X Safe Coordinator";
export const OWNER_ACCESS_DOMAIN_VERSION = "1";
export const OWNER_READ_PURPOSE = "Read private Safe labels and recovery drafts";
export const OWNER_MANAGE_PURPOSE = "Manage private Safe labels and recovery drafts";
// Stay below the server's five-minute maximum so small browser/server clock skew
// cannot make an otherwise fresh proof appear too far in the future.
export const OWNER_ACCESS_TTL_SECONDS = 240;

export const OWNER_ACCESS_TYPES = {
  SafeOwnerAccess: [
    { name: "owner", type: "address" },
    { name: "purpose", type: "string" },
    { name: "expiresAt", type: "uint256" },
  ],
};

export async function createOwnerAccessProof(
  config: AppConfig,
  signer: JsonRpcSigner,
  owner: string,
  purpose = OWNER_READ_PURPOSE,
): Promise<OwnerAccessProof> {
  const checkedOwner = getAddress(owner);
  const expiresAt = String(Math.floor(Date.now() / 1_000) + OWNER_ACCESS_TTL_SECONDS);
  const signature = await signer.signTypedData(
    {
      name: OWNER_ACCESS_DOMAIN_NAME,
      version: OWNER_ACCESS_DOMAIN_VERSION,
      chainId: config.chainId,
    },
    OWNER_ACCESS_TYPES,
    {
      owner: checkedOwner,
      purpose,
      expiresAt: BigInt(expiresAt),
    },
  );
  return { owner: checkedOwner, expiresAt, signature };
}

export function ownerAccessProofIsFresh(
  proof: OwnerAccessProof | null,
  account: string,
): proof is OwnerAccessProof {
  return Boolean(
    proof &&
      proof.owner.toLowerCase() === account.toLowerCase() &&
      BigInt(proof.expiresAt) > BigInt(Math.floor(Date.now() / 1_000) + 5),
  );
}

export function readProvider(config: AppConfig): JsonRpcProvider {
  return new JsonRpcProvider(config.rpcUrl, config.chainId, { staticNetwork: true });
}

export function safeDomain(config: AppConfig, safeAddress: string) {
  return { chainId: config.chainId, verifyingContract: getAddress(safeAddress) };
}

export function hashSafeTransaction(
  config: AppConfig,
  safeAddress: string,
  transaction: SafeTransaction,
): string {
  return TypedDataEncoder.hash(safeDomain(config, safeAddress), SAFE_TX_TYPES, transaction);
}

export function proposalTransaction(proposal: Proposal): SafeTransaction {
  return {
    to: getAddress(proposal.to),
    value: BigInt(proposal.value),
    data: proposal.data,
    operation: Number(proposal.operation),
    safeTxGas: BigInt(proposal.safeTxGas),
    baseGas: BigInt(proposal.baseGas),
    gasPrice: BigInt(proposal.gasPrice),
    gasToken: getAddress(proposal.gasToken),
    refundReceiver: getAddress(proposal.refundReceiver),
    nonce: BigInt(proposal.nonce),
  };
}

export async function contractSafeTransactionHash(
  provider: JsonRpcProvider,
  safeAddress: string,
  transaction: SafeTransaction,
): Promise<string> {
  const safe = new Contract(safeAddress, SAFE_ABI, provider);
  return String(
    await safe.getTransactionHash(
      transaction.to,
      transaction.value,
      transaction.data,
      transaction.operation,
      transaction.safeTxGas,
      transaction.baseGas,
      transaction.gasPrice,
      transaction.gasToken,
      transaction.refundReceiver,
      transaction.nonce,
    ),
  );
}

export async function assertSafeTransactionHash(
  config: AppConfig,
  safeAddress: string,
  transaction: SafeTransaction,
  expectedHash?: string,
): Promise<string> {
  const local = hashSafeTransaction(config, safeAddress, transaction);
  if (expectedHash && local.toLowerCase() !== expectedHash.toLowerCase()) {
    throw new Error("Proposal fields do not match the stored Safe transaction hash.");
  }
  const onchain = await contractSafeTransactionHash(readProvider(config), safeAddress, transaction);
  if (onchain.toLowerCase() !== local.toLowerCase()) {
    throw new Error("Local EIP-712 hash did not match the Safe contract. Refusing to continue.");
  }
  return local;
}

export function parseSaltNonce(value: string): bigint {
  const clean = value.trim();
  if (!clean) throw new Error("Enter a salt.");
  if (/^[0-9]+$/.test(clean) || /^0x[0-9a-fA-F]+$/.test(clean)) {
    const salt = BigInt(clean);
    if (salt < 0n || salt >= 1n << 256n) throw new Error("Salt must fit in uint256.");
    return salt;
  }
  return BigInt(keccak256(toUtf8Bytes(clean)));
}

export function encodeSafeSetup(
  owners: string[],
  threshold: number,
  fallbackHandler: string,
): string {
  if (owners.length === 0) throw new Error("At least one owner is required.");
  const checksummed = owners.map((owner) => getAddress(owner));
  const forbiddenOwners = new Set([ZeroAddress.toLowerCase(), `0x${"0".repeat(39)}1`]);
  if (checksummed.some((owner) => forbiddenOwners.has(owner.toLowerCase()))) {
    throw new Error("The zero and Safe sentinel addresses cannot be owners.");
  }
  if (new Set(checksummed.map((owner) => owner.toLowerCase())).size !== checksummed.length) {
    throw new Error("Safe owners must be unique.");
  }
  if (threshold < 1 || threshold > checksummed.length) {
    throw new Error("Threshold must be between 1 and the number of owners.");
  }
  return new Interface(SAFE_ABI).encodeFunctionData("setup", [
    checksummed,
    threshold,
    ZeroAddress,
    "0x",
    getAddress(fallbackHandler),
    ZeroAddress,
    0,
    ZeroAddress,
  ]);
}

export async function predictSafeAddress(
  config: AppConfig,
  owners: string[],
  threshold: number,
  salt: string,
): Promise<string> {
  const initializer = encodeSafeSetup(
    owners,
    threshold,
    config.contracts.compatibilityFallbackHandler,
  );
  const factory = new Contract(
    config.contracts.safeProxyFactory,
    FACTORY_ABI,
    readProvider(config),
  );
  const proxyCreationCode = String(await factory.proxyCreationCode());

  // SafeProxyFactory.deployProxy appends the singleton as uint256(uint160(singleton)),
  // so it is left-padded to 32 bytes rather than appended as a raw 20-byte address.
  const deploymentData = concat([
    proxyCreationCode,
    toBeHex(BigInt(config.contracts.safeSingleton), 32),
  ]);
  const create2Salt = keccak256(
    solidityPacked(
      ["bytes32", "uint256"],
      [keccak256(initializer), parseSaltNonce(salt)],
    ),
  );

  return getAddress(
    getCreate2Address(
      config.contracts.safeProxyFactory,
      create2Salt,
      keccak256(deploymentData),
    ),
  );
}

export interface CreatedSafe {
  address: string;
  owners: string[];
  threshold: number;
  transactionHash: string;
}

export async function createSafeProxy(
  config: AppConfig,
  signer: JsonRpcSigner,
  owners: string[],
  threshold: number,
  salt: string,
): Promise<CreatedSafe> {
  const initializer = encodeSafeSetup(
    owners,
    threshold,
    config.contracts.compatibilityFallbackHandler,
  );
  const factory = new Contract(config.contracts.safeProxyFactory, FACTORY_ABI, signer);
  const transaction = await factory.createProxyWithNonce(
    config.contracts.safeSingleton,
    initializer,
    parseSaltNonce(salt),
  );
  const receipt = await transaction.wait();
  if (!receipt || receipt.status !== 1) throw new Error("Safe creation transaction failed.");

  const factoryInterface = new Interface(FACTORY_ABI);
  let safeAddress = "";
  for (const log of receipt.logs) {
    if (getAddress(log.address) !== getAddress(config.contracts.safeProxyFactory)) continue;
    try {
      const parsed = factoryInterface.parseLog(log);
      if (parsed?.name === "ProxyCreation") {
        safeAddress = getAddress(String(parsed.args[0]));
        break;
      }
    } catch {
      // Ignore logs emitted by the Safe initializer.
    }
  }
  if (!safeAddress) throw new Error("ProxyCreation event was not found in the receipt.");

  const safe = new Contract(safeAddress, SAFE_ABI, readProvider(config));
  const [readOwners, readThreshold, version, bytecode] = await Promise.all([
    safe.getOwners() as Promise<string[]>,
    safe.getThreshold() as Promise<bigint>,
    safe.VERSION() as Promise<string>,
    readProvider(config).getCode(safeAddress),
  ]);
  if (bytecode === "0x") throw new Error("The created Safe has no runtime bytecode.");
  if (version !== "1.4.1") throw new Error(`Created proxy reported unexpected Safe version ${version}.`);

  const verifiedOwners = readOwners.map(getAddress);
  const expectedOwners = owners.map(getAddress);
  if (
    verifiedOwners.length !== expectedOwners.length ||
    verifiedOwners.some((owner, index) => owner !== expectedOwners[index]) ||
    Number(readThreshold) !== threshold
  ) {
    throw new Error("On-chain owner/threshold verification did not match the requested Safe.");
  }

  return {
    address: safeAddress,
    owners: verifiedOwners,
    threshold: Number(readThreshold),
    transactionHash: receipt.hash,
  };
}

export async function tokenMetadata(
  provider: JsonRpcProvider,
  tokenAddress: string,
): Promise<{ address: string; symbol: string; decimals: number }> {
  const address = getAddress(tokenAddress);
  const token = new Contract(address, ERC20_ABI, provider);
  const [symbol, decimals] = await Promise.all([token.symbol(), token.decimals()]);
  const parsedDecimals = Number(decimals);
  if (!Number.isInteger(parsedDecimals) || parsedDecimals < 0 || parsedDecimals > 255) {
    throw new Error("Token returned invalid decimals.");
  }
  return { address, symbol: String(symbol), decimals: parsedDecimals };
}

async function tokenBalance(
  provider: JsonRpcProvider,
  tokenAddress: string,
  safeAddress: string,
): Promise<TokenBalance> {
  const metadata = await tokenMetadata(provider, tokenAddress);
  const token = new Contract(metadata.address, ERC20_ABI, provider);
  const balance = (await token.balanceOf(safeAddress)) as bigint;
  return { ...metadata, balance };
}

export async function getSafeSnapshot(
  config: AppConfig,
  safeAddress: string,
  tokenAddresses: string[],
): Promise<SafeSnapshot> {
  const address = getAddress(safeAddress);
  const provider = readProvider(config);
  const bytecode = await provider.getCode(address);
  if (bytecode === "0x") throw new Error("No contract is deployed at this address.");
  const safe = new Contract(address, SAFE_ABI, provider);

  const uniqueTokens = [...new Set(tokenAddresses.filter(isAddress).map((token) => getAddress(token)))];
  const [version, owners, threshold, nonce, nativeBalance, tokenResults] = await Promise.all([
    safe.VERSION() as Promise<string>,
    safe.getOwners() as Promise<string[]>,
    safe.getThreshold() as Promise<bigint>,
    safe.nonce() as Promise<bigint>,
    provider.getBalance(address),
    Promise.allSettled(uniqueTokens.map((token) => tokenBalance(provider, token, address))),
  ]);
  if (version !== "1.4.1") {
    throw new Error(`Contract reports Safe version ${version}; this interface requires 1.4.1.`);
  }

  return {
    address,
    version,
    owners: owners.map(getAddress),
    threshold: Number(threshold),
    nonce,
    nativeBalance,
    tokens: tokenResults
      .filter((result): result is PromiseFulfilledResult<TokenBalance> => result.status === "fulfilled")
      .map((result) => result.value),
  };
}

export function encodeErc20Transfer(recipient: string, amount: bigint): string {
  return new Interface(ERC20_ABI).encodeFunctionData("transfer", [getAddress(recipient), amount]);
}

export function recoverSafeSigner(safeTxHash: string, signature: string): string {
  if (dataLength(signature) !== 65) throw new Error("Safe signatures must be 65 bytes.");
  const bytes = getBytes(signature);
  const v = bytes[64];
  let digest = safeTxHash;
  if (v === 31 || v === 32) {
    bytes[64] = v - 4;
    digest = hashMessage(getBytes(safeTxHash));
  } else if (v === 0 || v === 1) {
    // Some clients serialise EIP-712 recovery as 0/1. Safe expects 27/28.
    bytes[64] = v + 27;
  } else if (v !== 27 && v !== 28) {
    throw new Error("Unsupported Safe signature recovery byte.");
  }
  return getAddress(recoverAddress(digest, Signature.from(hexlify(bytes))));
}

export function normaliseSafeSignature(safeTxHash: string, signature: string): string {
  if (dataLength(signature) !== 65) throw new Error("Safe signatures must be 65 bytes.");
  const bytes = getBytes(signature);
  if (bytes[64] === 0 || bytes[64] === 1) bytes[64] += 27;
  // Recovery validates r/s/v and ensures the result is a usable EOA signature.
  recoverSafeSigner(safeTxHash, hexlify(bytes));
  return hexlify(bytes);
}

export function uniqueValidSignatures(
  safeTxHash: string,
  signatures: ProposalSignature[],
  owners: string[],
): Array<ProposalSignature & { recoveredSigner: string }> {
  const ownerSet = new Set(owners.map((owner) => owner.toLowerCase()));
  const seen = new Set<string>();
  const valid: Array<ProposalSignature & { recoveredSigner: string }> = [];
  for (const item of signatures) {
    try {
      const signature = normaliseSafeSignature(safeTxHash, item.signature);
      const recoveredSigner = recoverSafeSigner(safeTxHash, signature);
      const key = recoveredSigner.toLowerCase();
      if (!ownerSet.has(key) || seen.has(key)) continue;
      seen.add(key);
      valid.push({ ...item, signature, recoveredSigner });
    } catch {
      // Invalid signatures are shown as missing and are never passed to the Safe.
    }
  }
  return valid;
}

export function packSortedSignatures(
  safeTxHash: string,
  signatures: ProposalSignature[],
  owners: string[],
): string {
  const valid = uniqueValidSignatures(safeTxHash, signatures, owners).sort((left, right) => {
    const a = BigInt(left.recoveredSigner);
    const b = BigInt(right.recoveredSigner);
    return a < b ? -1 : a > b ? 1 : 0;
  });
  return `0x${valid.map((item) => item.signature.slice(2)).join("")}`;
}

export async function executeProposal(
  config: AppConfig,
  signer: JsonRpcSigner,
  safeAddress: string,
  proposal: Proposal,
  owners: string[],
): Promise<string> {
  const safeTransaction = proposalTransaction(proposal);
  await assertSafeTransactionHash(config, safeAddress, safeTransaction, proposal.safeTxHash);
  const signatures = packSortedSignatures(proposal.safeTxHash, proposal.signatures, owners);
  const safe = new Contract(safeAddress, SAFE_ABI, signer);
  const transaction = await safe.execTransaction(
    safeTransaction.to,
    safeTransaction.value,
    safeTransaction.data,
    safeTransaction.operation,
    safeTransaction.safeTxGas,
    safeTransaction.baseGas,
    safeTransaction.gasPrice,
    safeTransaction.gasToken,
    safeTransaction.refundReceiver,
    signatures,
  );
  const receipt = await transaction.wait();
  if (!receipt || receipt.status !== 1) throw new Error("Safe execution transaction failed.");
  const safeInterface = new Interface(SAFE_ABI);
  let executionSucceeded = false;
  for (const log of receipt.logs) {
    if (getAddress(log.address) !== getAddress(safeAddress)) continue;
    let parsed;
    try {
      parsed = safeInterface.parseLog(log);
    } catch {
      // Ignore non-Safe logs and events outside this minimal ABI.
      continue;
    }
    if (parsed?.name === "ExecutionFailure" && String(parsed.args[0]).toLowerCase() === proposal.safeTxHash.toLowerCase()) {
      throw new Error("The Safe transaction was mined but its inner call failed.");
    }
    if (parsed?.name === "ExecutionSuccess" && String(parsed.args[0]).toLowerCase() === proposal.safeTxHash.toLowerCase()) {
      executionSucceeded = true;
    }
  }
  if (!executionSucceeded) {
    throw new Error("Safe execution receipt did not contain the expected ExecutionSuccess event.");
  }
  return receipt.hash;
}
